import styled, { css, CSSObject } from 'styled-components';

import Pdf from 'dashboard/components/Pdf';
import { BackgroundColors } from 'design-system/theme';

interface ImageProps {
  width?: number;
  height?: number;
  isLoaded: boolean;
  display?: 'inline' | 'block';
  position: 'relative' | 'absolute';
  background?: keyof BackgroundColors | null;
  rotate?: number;
  scale?: number;
}

export const Image = styled('img')<ImageProps>`
  height: ${({ height }) => (height ? `${height}px` : '100%')};
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  // NOTE this is not supported by IE but we're ok with that
  object-fit: contain;
  opacity: ${({ isLoaded }) => (isLoaded ? '1' : '0')};
  transition: opacity 0.5s;
  display: ${({ display = 'inline' }) => display};
  position: ${({ position }) => position};
  background: ${({ theme, background = 'secondary' }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return theme.color.background[background];
  }};
  ${({ rotate, scale = 1 }) => {
    if (rotate == null || rotate === 0) {
      return css`
        transform-origin: top center;
        transform: scale(${scale});
      `;
    }
    // we use the following transform to make sure the rotated image is positioned in the same place
    // as the non-rotated image.
    const styling: CSSObject = {
      transform: `translateY(-100%) rotate(${rotate}deg) scale(${scale})`,
    };
    if (rotate === 90) {
      styling['transformOrigin'] = 'bottom left';
    } else if (rotate === 180) {
      styling['transformOrigin'] = 'bottom center';
    } else if (rotate === 270) {
      styling['transformOrigin'] = 'bottom right';
    }
    return css(styling);
  }}
`;

export interface FileInfo {
  url: string;
  name?: string;
  type?: string;
  /**
   * If present, will override `modalLabel`
   */
  label?: string;
}

export function isPdf(file: FileInfo) {
  return file.type === 'application/pdf' || file.name?.endsWith('.pdf');
}

interface FileDisplayProps {
  'data-test': string;
  file: FileInfo;
  loading: boolean;
  onError: () => void;
  onFileLoad: (numPages?: number) => void;
  onPageLoad?: () => void;
  thumbnail?: boolean;
  width?: number;
  height?: number;
  background?: keyof BackgroundColors;
  position?: 'relative' | 'absolute'; // used for images only
  fileRef?: Array<HTMLImageElement | HTMLCanvasElement>;
  fileStyleRef?: Array<HTMLImageElement | HTMLDivElement>;
  referrerPolicy?: 'no-referrer' | 'origin' | 'unsafe-url';
  renderPdfTextLayer?: boolean;
  renderAnnotationLayer?: boolean;
  rotate?: number;
  scale?: number;
}

const FileDisplay = ({
  'data-test': dataTest,
  file,
  loading,
  onError,
  onFileLoad,
  onPageLoad,
  thumbnail,
  width,
  height,
  background,
  fileRef,
  fileStyleRef,
  position = 'relative',
  referrerPolicy,
  renderPdfTextLayer = true,
  renderAnnotationLayer = true,
  rotate,
  scale,
}: FileDisplayProps) => {
  if (isPdf(file)) {
    if (thumbnail) {
      // We only pass height because aspect ratio is locked. Most PDFs are
      // taller than they are wide, so passing height makes it less likely for
      // the preview to break outside the bounding box.
      return (
        <Pdf
          src={file.url}
          onFileLoad={onFileLoad}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onPageLoad={onPageLoad}
          onError={onError}
          height={height}
          pagesToShow={1}
          background={background}
          fileRef={fileRef}
          fileStyleRef={fileStyleRef}
          renderPdfTextLayer={renderPdfTextLayer}
          renderAnnotationLayer={renderAnnotationLayer}
          rotate={rotate}
          scale={scale}
        />
      );
    } else {
      // If both height and width is passed in, height is ignored by react-pdf
      return (
        <Pdf
          src={file.url}
          onFileLoad={onFileLoad}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onPageLoad={onPageLoad}
          onError={onError}
          width={width}
          height={height}
          background={background}
          fileRef={fileRef}
          fileStyleRef={fileStyleRef}
          renderPdfTextLayer={renderPdfTextLayer}
          renderAnnotationLayer={renderAnnotationLayer}
          rotate={rotate}
          scale={scale}
        />
      );
    }
  } else {
    return (
      <Image
        data-test={dataTest}
        src={file.url}
        width={width}
        height={height}
        isLoaded={!loading}
        onLoad={() => {
          onFileLoad(/* numPages */ 1);
          onPageLoad && onPageLoad();
        }}
        onError={onError}
        background={background}
        position={position}
        referrerPolicy={referrerPolicy}
        rotate={rotate}
        scale={scale}
        ref={(x) => {
          if (fileRef) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            fileRef[0] = x;
          }
          if (fileStyleRef) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            fileStyleRef[0] = x;
          }
        }}
      />
    );
  }
};

export default FileDisplay;
